import React, { Fragment, memo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import type { LmsTrainingPlain } from 'src/interfaces/collectStepReference'

interface Props {
  isOpen: boolean
  lmsTraining: LmsTrainingPlain | null
  isDeleting: boolean
  onConfirm(): void
  onClose(): void
}

const DeleteLmsTrainingModalComponent = ({
  isOpen,
  lmsTraining,
  isDeleting,
  onConfirm,
  onClose,
}: Props) => {
  if (!lmsTraining) {
    return null
  }

  return (
    <CommonModal
      isOpen={isOpen}
      title="Delete this course?"
      onClose={onClose}
      buttons={
        <Fragment>
          {/* Disabled while the request is in flight so the control matches the page's
              dismissal guard — a Cancel that looks clickable but does nothing is worse. */}
          <Button size="small" color="grey" disabled={isDeleting} onClick={onClose}>
            Cancel
          </Button>
          {/* `red` is the shared Button's destructive variant, ported from apps/dashboard: a pale
              critical background with critical-500 text. The colour carries the warning
              alongside the title, body and label. */}
          <Button
            size="small"
            color="red"
            loading={isDeleting}
            disabled={isDeleting}
            onClick={onConfirm}
            data-testid="lms-training-delete-confirm-button"
          >
            Delete course
          </Button>
        </Fragment>
      }
    >
      <div css={tw`flex flex-col gap-2`}>
        <Typography variant="body2">
          {lmsTraining.title} will be permanently removed from the global catalog. There is no
          archive and this cannot be undone.
        </Typography>
        {/* The FK is on the per-contractor runtime rows (lms-training-collect-data-step), not on
            the template's jsonb metadata — so a template-only reference does NOT block the
            delete. Say so plainly rather than implying a guard that does not exist. */}
        <Typography variant="body2" css={tw`text-shades-500`}>
          There is no in-use check on this endpoint. A course still referenced by a customer&apos;s
          onboarding template will be deleted anyway, leaving that template broken — remove it from
          those templates first. The delete will only fail outright if a contractor has already
          started the course.
        </Typography>
      </div>
    </CommonModal>
  )
}

export const DeleteLmsTrainingModal = memo(DeleteLmsTrainingModalComponent)
